import React from 'react';
import { Phase } from '../data/mock';
import Tooltip from './Tooltip';

interface PhasePipelineProps {
  phases: Phase[];
  isPhaseUnlocked: (id: string) => boolean;
  isPhaseComplete: (id: string) => boolean;
  getProgress: (id: string) => number;
  currentPhaseId?: string;
  className?: string;
  phase0?: { isSubmitted: boolean; onClick: () => void };
  finalPitch?: { onClick: () => void; isGraded?: boolean };
}

const PhasePipeline: React.FC<PhasePipelineProps> = ({
  phases,
  isPhaseUnlocked,
  isPhaseComplete,
  getProgress,
  currentPhaseId,
  className = '',
  phase0,
  finalPitch,
}) => {
  const sortedPhases = [...phases].sort((a, b) => a.order - b.order);

  const getPhaseIcon = (phase: Phase) => {
    if (isPhaseComplete(phase.id)) {
      return (
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    if (!isPhaseUnlocked(phase.id)) {
      return (
        <svg className="w-5 h-5 text-muted" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
      );
    }
    return <span className="text-sm font-bold text-white">{phase.order}</span>;
  };

  const getPhaseTooltip = (phase: Phase): string => {
    if (isPhaseComplete(phase.id)) return 'Completed';
    if (phase.id === currentPhaseId) return 'In progress';
    if (isPhaseUnlocked(phase.id)) {
      const pct = getProgress(phase.id);
      return pct > 0 ? `In progress — ${pct}%` : 'Unlocked';
    }
    return 'Locked — complete previous phases first';
  };

  return (
    <div className={`flex items-center gap-2 overflow-x-auto pb-2 ${className}`}>
      {/* Phase 0 — Initial Pitch */}
      {phase0 && (
        <>
          <button
            onClick={phase0.onClick}
            className={`flex flex-col items-center gap-2 min-w-[120px] flex-shrink-0 p-3 rounded-xl transition-all cursor-pointer ${
              phase0.isSubmitted
                ? 'bg-success/10 border border-success/20 hover:bg-success/15'
                : 'bg-warning/10 border border-warning/20 hover:bg-warning/15'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${phase0.isSubmitted ? 'bg-success' : 'bg-warning'}`}>
              {phase0.isSubmitted ? (
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <span className="text-sm font-bold text-white">0</span>
              )}
            </div>
            <span className="text-xs font-medium text-center leading-tight text-text">Initial Pitch</span>
          </button>
          {sortedPhases.length > 0 && (
            <div className={`h-0.5 w-8 flex-shrink-0 ${phase0.isSubmitted ? 'bg-success' : 'bg-line'}`} />
          )}
        </>
      )}

      {/* Regular phases — display only, no navigation */}
      {sortedPhases.map((phase, index) => {
        const completed = isPhaseComplete(phase.id);
        const unlocked = isPhaseUnlocked(phase.id);
        const isCurrent = phase.id === currentPhaseId;
        const progress = getProgress(phase.id);

        return (
          <React.Fragment key={phase.id}>
            {index > 0 && (
              <div className={`h-0.5 w-8 flex-shrink-0 ${completed ? 'bg-success' : 'bg-line'}`} />
            )}
            <Tooltip content={getPhaseTooltip(phase)} position="bottom">
              <div
                className={`flex flex-col items-center gap-2 min-w-[120px] flex-shrink-0 p-3 rounded-xl transition-all cursor-default ${
                  completed
                    ? 'bg-success/10 border border-success/20'
                    : isCurrent
                    ? 'bg-chart/10 border border-chart/20'
                    : unlocked
                    ? 'bg-surface border border-line'
                    : 'bg-surface/50 border border-line/50 opacity-60'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  completed ? 'bg-success' : isCurrent ? 'bg-chart animate-pulse' : unlocked ? 'bg-muted' : 'bg-surface border-2 border-line'
                }`}>
                  {getPhaseIcon(phase)}
                </div>
                <span className={`text-xs font-medium text-center leading-tight ${completed || isCurrent ? 'text-text' : 'text-muted'}`}>
                  {phase.title}
                </span>
                {unlocked && !completed && progress > 0 && (
                  <div className="w-full bg-line rounded-full h-1">
                    <div className="bg-chart h-1 rounded-full" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
            </Tooltip>
          </React.Fragment>
        );
      })}

      {/* Final Pitch — Phase N+1 */}
      {finalPitch && (
        <>
          {sortedPhases.length > 0 && (
            <div className={`h-0.5 w-8 flex-shrink-0 ${finalPitch.isGraded ? 'bg-success' : 'bg-success'}`} />
          )}
          <button
            onClick={finalPitch.onClick}
            className={`flex flex-col items-center gap-2 min-w-[120px] flex-shrink-0 p-3 rounded-xl transition-all cursor-pointer ${
              finalPitch.isGraded
                ? 'bg-success/10 border border-success/20 hover:bg-success/15'
                : 'bg-chart/10 border border-chart/30 hover:bg-chart/15'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${finalPitch.isGraded ? 'bg-success' : 'bg-chart'}`}>
              {finalPitch.isGraded ? (
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
            </div>
            <span className="text-xs font-medium text-center leading-tight text-text">Final Pitch</span>
          </button>
        </>
      )}
    </div>
  );
};

export default PhasePipeline;
